import { motion } from 'framer-motion';
import { Colors } from '../../themes/colors';
import { Text } from '../Text';
import { Box } from '../Box';
import { Center } from '../Center';
import { ReactNode } from 'react';

interface ITabProps {
  PrefixComponent?: ReactNode;
  title: string;
  badge?: string | number;
  isActive: boolean;
  setActiveTab: () => void;
  textStyle?: React.CSSProperties;
  totalCount?: number;
  totalCountStyle?: React.CSSProperties;
  totalCountTextStyle?: React.CSSProperties;
  isNew?: boolean;
  isVisible?: boolean;
  dataCy?: string;
  badgeCy?: string;
}

const borderColorVariants = {
  active: {
    borderColor: Colors.secondary_500,
  },
  inactive: {
    borderColor: Colors.white,
  },
};

const spanVariants = {
  active: {
    fontWeight: 700,
  },
  inactive: {
    fontWeight: 400,
  },
};

export const Tab = ({
  PrefixComponent,
  title,
  badge,
  isActive,
  setActiveTab,
  textStyle = {},
  totalCount,
  totalCountStyle = {},
  totalCountTextStyle = {},
  isNew = false,
  isVisible = true,
  dataCy,
  badgeCy,
}: ITabProps) => {
  if (!isVisible) {
    return null;
  }

  return (
    <button
      key={isActive ? 'active' : 'inactive'}
      type="button"
      data-cy={dataCy}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setActiveTab();
      }}
      style={{
        all: 'unset',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {PrefixComponent && <Box marginBottom={3}>{PrefixComponent}</Box>}
        <motion.div
          initial="inactive"
          transition={{ duration: 0.3 }}
          animate={isActive ? 'active' : 'inactive'}
          variants={borderColorVariants}
          style={{
            paddingRight: 10,
            paddingLeft: 10,
            borderBottomWidth: 3,
            borderBottomStyle: 'solid',
            position: 'relative',
          }}
        >
          <motion.span
            initial="inactive"
            transition={{ duration: 0.1 }}
            animate={isActive ? 'active' : 'inactive'}
            variants={spanVariants}
            style={{
              fontFamily: 'Nunito',
              display: 'flex',
              alignItems: 'center',
              color: isActive ? Colors.neutral_600 : Colors.neutral_300,
              fontSize: 22,
              paddingBottom: 4,
              ...textStyle,
            }}
          >
            {title}
            {!!badge && (
              <span
                data-cy={badgeCy}
                style={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Nunito',
                  backgroundColor: Colors.error_500,
                  color: Colors.white,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: 2,
                  minWidth: 16,
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  top: -4,
                  right: -8,
                }}
              >
                {badge}
              </span>
            )}
          </motion.span>
        </motion.div>

        {totalCount != null && (
          <Box
            borderRadius={3}
            marginBottom={5}
            paddingHorizontal={7}
            paddingVertical={2}
            style={{
              backgroundColor: isActive ? Colors.primary_500 : Colors.primary_100,
              ...totalCountStyle,
            }}
          >
            <Text
              bold
              fontSize={14}
              style={{
                color: isActive ? Colors.white : Colors.primary_500,
                ...totalCountTextStyle,
              }}
            >
              {totalCount}
            </Text>
          </Box>
        )}

        {isNew && (
          <Center
            backgroundColor={Colors.secondary_500}
            width={48}
            height={20}
            borderRadius={3}
            marginBottom={8}
            marginLeft={8}
          >
            <Text bold fontSize={12} color="white">
              NEW
            </Text>
          </Center>
        )}
      </div>
    </button>
  );
};

Tab.displayName = 'Tab';
